import { Box, Button, Modal, Stack, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from "react-i18next";
import BgBox from '../../../components/BgBox';
import CustomDropDown from '../../../components/CustomDropDown';
import CustomRadio from '../../../components/CustomRadio';
import CtrlFillLabelTxtField from '../../../components/CtrlFillLabelTxtField';
import LabelTxt from '../../../components/LabelTxt';
import LoadingScreen from '../../../components/loadingScreen';
import { customAlert, notify } from '../../../components/notify';
import ScrollBox from '../../../components/ScrollBox';
import { useAuthContext } from '../../../context/AuthContext';
import QueryKey from '../../../QueryKey';
import route from '../../../Routes';
import { getAllTrkOpReqWPagSrch, getSingleCustReq, getSingleTrkOpReq } from '../../../services/req_service';
import { getAllTrksByTpId } from '../../../services/trkOp-service';
import { postCombinedShipmts } from '../../../services/shipmt-service';
import { capFirstLetter, datePickerformat, getLocalStrg, makeTxtOverFLow } from '../../../utils/format';
import { getShipmtFormattedData } from '../../../utils/shipmtDataFormat';
import TrkOpReqTablePopup from '../AddShipmtPage/TrkOpReqTablePopup';

const sessArr = [
    { value: "morning", label: "Morning" },
    { value: "noon", label: "Noon" },
    { value: "evening", label: "Evening" },
    { value: "night", label: "Night" }];

function AddCombShipmtPage() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { setLoadingScreen } = useAuthContext();
    const [searchParams] = useSearchParams();
    const custReqIds = (searchParams.get("custReqIds") || "").split(",").filter(Boolean);

    const [trkOpReqId, setTrkOpReqId] = useState("");
    const [pickerOpen, setPickerOpen] = useState(true);
    const [formErrors, setFormErrors] = useState({});
    const [selection, setSelection] = useState({});
    // Per-request editable fields (dates, incharge details), keyed by custReqId.
    const [reqDates, setReqDates] = useState({});

    const { isLoading, isError, error, data: custReqs } = useQuery(
        [QueryKey.singleCustReq, "comb", custReqIds.join(",")],
        () => Promise.all(custReqIds.map((id) => getSingleCustReq(id))),
        { enabled: custReqIds.length > 0, refetchOnWindowFocus: false });

    const { data: tpReqData } = useQuery(
        [QueryKey.singleTrkOpReq, trkOpReqId],
        () => getSingleTrkOpReq(trkOpReqId),
        { enabled: Boolean(trkOpReqId), refetchOnWindowFocus: false });

    const { data: trksData } = useQuery(
        [QueryKey.allTrks, tpReqData?.truckopId],
        () => getAllTrksByTpId(tpReqData?.truckopId),
        { enabled: Boolean(tpReqData?.truckopId), refetchOnWindowFocus: false });

    // Picker rows: all current truck operator requests.
    const { data: trkOpReqRows } = useQuery(
        [QueryKey.trkOpReqList, "combPicker"],
        () => getAllTrkOpReqWPagSrch({ pageNumber: 1, limit: 100, order: "desc", time: "current" }),
        { enabled: pickerOpen });

    useEffect(() => {
        if (!custReqs) return;
        setReqDates((prev) => {
            const next = { ...prev };
            custReqs.forEach((r) => {
                if (!next[r.custReqId])
                    next[r.custReqId] = { estPicDate: datePickerformat(r.picDate), estDelDate: datePickerformat(r.delDate) };
            });
            return next;
        });
    }, [custReqs]);

    useEffect(() => {
        if (!trksData || !tpReqData) return;
        const trkObj = trksData.find((i) => i.id === tpReqData.truckId);
        setSelection((e) => ({ ...e, truckId: tpReqData.truckId, trkObj }));
    }, [trksData, tpReqData]);

    function handlSelChange(e) {
        const { name, value } = e.target;
        if (name === "truckId") {
            const trkObj = trksData?.find((i) => i.id === value);
            setSelection((prev) => ({ ...prev, truckId: value, trkObj }));
        } else {
            setSelection((prev) => ({ ...prev, [name]: value }));
        }
    }

    function handleReqFieldChange(custReqId, name, value) {
        setReqDates((prev) => ({ ...prev, [custReqId]: { ...prev[custReqId], [name]: value } }));
    }

    function handlePickerSelect(selTrkOpReqId) {
        setTrkOpReqId(selTrkOpReqId);
        setPickerOpen(false);
    }

    // Total selected weight in tons vs the truck's remaining capacity.
    const totalWeightTons = (custReqs || []).reduce((s, r) => {
        const w = Number(r.weight) || 0;
        return s + (r.weightUnit === 'kg' ? w / 1000 : w);
    }, 0);
    const availCap = selection?.trkObj ? (Number(selection.trkObj.maxCap) || 0) - (Number(tpReqData?.filledCap) || 0) : null;
    const overCapacity = availCap !== null && availCap >= 0 && totalWeightTons > availCap;

    function buildFlatObject(req) {
        const dates = reqDates[req.custReqId] || {};
        const isKg = req.weightUnit === 'kg';
        return {
            custId: req.custId,
            truckOpId: tpReqData?.truckopId,
            custReqId: req.custReqId,
            truckOpReqId: trkOpReqId,
            trkOpDataFilledBy: "backoffice",
            custDataFilledBy: "backoffice",

            // These shipmt_pic_del columns are NOT NULL - default to "" like the single form does.
            cnorName: req.cnorName || "", cnorMobile: req.cnorMobile, cnorEmail: req.cnorEmail,
            cneeName: req.cneeName || "", cneeMobile: req.cneeMobile, cneeEmail: req.cneeEmail,
            picLocation: req.picLocation || "", picAddress: req.picAddress || "",
            delLocation: req.delLocation || "", delAddress: req.delAddress || "",
            estPicDate: dates.estPicDate, estDelDate: dates.estDelDate,
            estPicSession: selection.estPicSession, estDelSession: selection.estDelSession,

            matCategory: req.matCategory,
            matType: req.matType,
            matTypeFreeTxt: req.matType === 'Others' ? req.matTypeFreeTxt : null,
            matNature: req.matNature,
            matNatureFreeTxt: req.matNature === 'others' ? req.matNatureFreeTxt : null,
            loadType: req.loadType,
            quantity: req.quantity,
            loadDesc: req.loadDesc,
            matDimsUnit: req.matCategory === 'weight' ? null : req.matDimsUnit,
            matLength: req.matCategory === 'weight' ? null : req.matLength,
            matWidth: req.matCategory === 'weight' ? null : req.matWidth,
            matHeight: req.matCategory === 'weight' ? null : req.matHeight,
            weight: req.weightUnit === 'kg' ? (parseFloat(req.weight) / 1000) : req.weight,
            weightUnit: isKg ? "ton" : req.weightUnit,

            drivName: selection.drivName, drivMobile1: selection.drivMobile1, drivMobile2: selection.drivMobile2,
            drivLicNo: selection.drivLicNo, trackingGPSLink: selection.trackingGPSLink,
            picIncName: dates.picIncName, picInchMobile1: dates.picInchMobile1, picInchMobile2: dates.picInchMobile2,
            delInchName: dates.delInchName, delInchMobile1: dates.delInchMobile1, delInchMobile2: dates.delInchMobile2,

            truckId: selection.truckId,
            truckRegNo: selection?.trkObj?.regNo,
            trkPermitType: selection?.trkObj?.permit_type,
            trkIns_expDate: datePickerformat(selection?.trkObj?.ins_expDate),
            trkFc_expDate: datePickerformat(selection?.trkObj?.fc_expDate),
            trkMaxCap: selection?.trkObj?.maxCap,
            trkMaxCapUnit: selection?.trkObj?.maxCapUnit,
            truck_type: selection?.trkObj?.type,

            billCopy: req.step3?.billCopy,
            ewayA: req.step3?.ewayA,
            ewayA_no: req.step3?.ewayA_no,
            lrCopy: req.step3?.lrCopy,
            lrNum: req.step3?.lrNum,
            cnorGST: req.step3?.cnorGST || "",
            cneeGST: req.step3?.cneeGST || "",
            cust_finalPrc: req.step3?.cust_finalPrc ?? req.estPrice,
            custPayType: req.step3?.custPayType ?? req.payType,
        };
    }

    function validate() {
        const errors = {};
        if (!selection.truckId) errors.truckId = "Truck is required!";
        if (!selection.drivName) errors.drivName = "Driver Name is required!";
        if (!selection.drivMobile1) errors.drivMobile1 = "Driver Mobile is required!";
        else if (String(selection.drivMobile1).length !== 10) errors.drivMobile1 = "Mobile Number should contain 10 numbers";
        if (!selection.estPicSession) errors.estPicSession = "Pickup Session is required!";
        if (!selection.estDelSession) errors.estDelSession = "Delivery Session is required!";
        (custReqs || []).forEach((r) => {
            const d = reqDates[r.custReqId] || {};
            if (!d.estPicDate || !d.estDelDate)
                errors[`dates_${r.custReqId}`] = "Both dates are required!";
        });
        return errors;
    }

    async function handleSubmit() {
        const errors = validate();
        setFormErrors(errors);
        if (Object.keys(errors).length > 0) {
            notify("error", "Please fill the required trip details");
            return;
        }

        setLoadingScreen(true);
        try {
            const shipments = custReqs.map((req) => getShipmtFormattedData(buildFlatObject(req)));
            const res = await postCombinedShipmts({ shipments });

            if (res?.created > 0)
                notify("success", `${res.created} shipment(s) created successfully`);
            (res?.results || []).filter(r => !r.success).forEach(r =>
                notify("error", `${r.custReqId || "Request"}: shipment creation failed`));

            if (res?.failed === 0)
                navigate(route.boShipmtList + "current");
        } catch (err) {
            customAlert(err);
        }
        setLoadingScreen(false);
    }

    if (isError) {
        customAlert(error);
        return <h2>Something went wrong</h2>
    }

    if (isLoading)
        return <Box sx={{ position: "absolute", top: 0, left: 0 }}><LoadingScreen /></Box>

    let txtFieldProps = { fontSize: 14, height: 38, width: 240 };
    let titleStyle = { fontWeight: "bold", fontSize: 16, color: "rgba(131, 146, 171, 0.7)" };
    const trksDD = (trksData || []).map((i) => ({ value: i.id, label: i.regNo }));

    return (
        <BgBox px={4} py={2}>
            <Stack direction={'row'} justifyContent="space-between" alignItems={"center"} mb={1} px={1}>
                <Typography variant='h5' sx={{ fontWeight: "bold" }}>{t("bo.combShipmt.title")} ({custReqIds.length})</Typography>
                <Stack direction={'row'} alignItems={"center"} gap={2}>
                    {trkOpReqId &&
                        <Typography sx={{ fontSize: 14, fontWeight: 600, color: "primary.main" }}>{trkOpReqId}</Typography>}
                    <Button variant='outlined' onClick={() => setPickerOpen(true)}>
                        {trkOpReqId ? t("bo.combShipmt.changeTrkOpReq") : t("bo.combShipmt.selTrkOpReq")}
                    </Button>
                    <Button variant='contained' disabled={!trkOpReqId} onClick={handleSubmit}>{t("bo.combShipmt.create")}</Button>
                </Stack>
            </Stack>

            {overCapacity &&
                <Typography sx={{ color: "#B26A00", fontSize: 13, px: 1, mb: 1 }}>
                    {t("bo.combShipmt.capWarn")} ({getLocalStrg(totalWeightTons)} Ton &gt; {getLocalStrg(availCap)} Ton)
                </Typography>}

            <ScrollBox height={"88%"}>
                {trkOpReqId && <Stack px={1} mb={2}>
                    <Typography sx={titleStyle} mb={1.5}>{t("bo.combShipmt.tripDet")}</Typography>
                    <Stack direction={"row"} gap={3} flexWrap={"wrap"} mb={1}>
                        {trksDD.length > 0 &&
                            <CustomDropDown key={selection.truckId || "trk"} defaultValue={selection.truckId} title={t("bo.addShipmtPg.selTrk")} name={"truckId"} errMsg={formErrors.truckId} ddArr={trksDD} handleDDChange={handlSelChange} {...txtFieldProps} />}
                        <CtrlFillLabelTxtField name="drivName" value={selection.drivName || ""} onChange={handlSelChange} title={t("bo.addShipmtPg.drivName")} errMsg={formErrors.drivName} {...txtFieldProps} />
                        <CtrlFillLabelTxtField name="drivMobile1" value={selection.drivMobile1 || ""} onChange={handlSelChange} title={t("bo.addShipmtPg.drivMob1")} errMsg={formErrors.drivMobile1} {...txtFieldProps} />
                        <CtrlFillLabelTxtField name="drivMobile2" value={selection.drivMobile2 || ""} onChange={handlSelChange} title={t("bo.addShipmtPg.drivMob2")} {...txtFieldProps} />
                        <CtrlFillLabelTxtField name="drivLicNo" value={selection.drivLicNo || ""} onChange={handlSelChange} title={t("bo.addShipmtPg.drivLicNo")} {...txtFieldProps} />
                        <CtrlFillLabelTxtField name="trackingGPSLink" value={selection.trackingGPSLink || ""} onChange={handlSelChange} title={t("bo.addShipmtPg.trackGps")} {...txtFieldProps} />
                    </Stack>
                    <Stack direction={"row"} gap={5}>
                        <CustomRadio title={t("bo.addShipmtPg.pickSession")} name={"estPicSession"} errMsg={formErrors.estPicSession} radioArr={sessArr} handleRadioChange={handlSelChange} fontSize={14} gap={3} />
                        <CustomRadio title={t("bo.addShipmtPg.delSession")} name={"estDelSession"} errMsg={formErrors.estDelSession} radioArr={sessArr} handleRadioChange={handlSelChange} fontSize={14} gap={3} />
                    </Stack>
                </Stack>}

                <Typography sx={titleStyle} px={1} mb={1}>{t("bo.combShipmt.selReqs")}</Typography>
                <Stack gap={1.5} px={1}>
                    {(custReqs || []).map((req) => (
                        <Stack key={req.custReqId} sx={{ border: 1, borderColor: "rgba(131, 146, 171, 0.3)", borderRadius: 2, p: 2 }} gap={1}>
                            <Stack direction={"row"} justifyContent={"space-between"} alignItems={"center"}>
                                <Typography sx={{ fontWeight: "bold", fontSize: 15 }}>{req.custReqId}</Typography>
                                <Typography sx={{ fontWeight: "bold", fontSize: 15, color: "primary.main" }}>
                                    ₹ {getLocalStrg(req.step3?.cust_finalPrc ?? req.estPrice)}
                                </Typography>
                            </Stack>
                            <Stack direction={"row"} gap={4} flexWrap={"wrap"}>
                                <LabelTxt label={t("bo.combShipmt.cust")} value={`${capFirstLetter(req.custFName || '')} ${capFirstLetter(req.custLName || '')}`.trim() || "NA"} minWidth={140} maxWidth={180} />
                                <LabelTxt label={t("bo.filterMdl.cnor")} value={req.cnorName || "NA"} minWidth={140} maxWidth={180} />
                                <LabelTxt label={t("bo.filterMdl.cnee")} value={req.cneeName || "NA"} minWidth={140} maxWidth={180} />
                                <LabelTxt label={t("bo.combShipmt.fromTo")} value={`${makeTxtOverFLow(req.picLocation, 22)} → ${makeTxtOverFLow(req.delLocation, 22)}`} minWidth={220} maxWidth={320} />
                                <LabelTxt label={t("bo.combShipmt.matWeight")} value={`${capFirstLetter(req.matType || '')} - ${req.weight} ${req.weightUnit}`} minWidth={140} maxWidth={200} />
                            </Stack>
                            <Stack direction={"row"} gap={3} alignItems={"flex-end"}>
                                <CtrlFillLabelTxtField type={"date"} name={`estPicDate_${req.custReqId}`} title={t("bo.addShipmtPg.estPickDate")}
                                    value={reqDates[req.custReqId]?.estPicDate || ""} onChange={(e) => handleReqFieldChange(req.custReqId, "estPicDate", e.target.value)}
                                    errMsg={formErrors[`dates_${req.custReqId}`]} {...txtFieldProps} width={180} />
                                <CtrlFillLabelTxtField type={"date"} name={`estDelDate_${req.custReqId}`} title={t("bo.addShipmtPg.estDelDate")}
                                    value={reqDates[req.custReqId]?.estDelDate || ""} onChange={(e) => handleReqFieldChange(req.custReqId, "estDelDate", e.target.value)}
                                    {...txtFieldProps} width={180} />
                            </Stack>
                            <Stack direction={"row"} gap={3} flexWrap={"wrap"}>
                                <CtrlFillLabelTxtField name={`picIncName_${req.custReqId}`} title={t("bo.addShipmtPg.pickInchargeName")}
                                    value={reqDates[req.custReqId]?.picIncName || ""} onChange={(e) => handleReqFieldChange(req.custReqId, "picIncName", e.target.value)} {...txtFieldProps} />
                                <CtrlFillLabelTxtField name={`picInchMobile1_${req.custReqId}`} title={t("bo.addShipmtPg.pickInchargeMob1")}
                                    value={reqDates[req.custReqId]?.picInchMobile1 || ""} onChange={(e) => handleReqFieldChange(req.custReqId, "picInchMobile1", e.target.value)} {...txtFieldProps} />
                                <CtrlFillLabelTxtField name={`delInchName_${req.custReqId}`} title={t("bo.addShipmtPg.delInchargeName")}
                                    value={reqDates[req.custReqId]?.delInchName || ""} onChange={(e) => handleReqFieldChange(req.custReqId, "delInchName", e.target.value)} {...txtFieldProps} />
                                <CtrlFillLabelTxtField name={`delInchMobile1_${req.custReqId}`} title={t("bo.addShipmtPg.delInchargeMob1")}
                                    value={reqDates[req.custReqId]?.delInchMobile1 || ""} onChange={(e) => handleReqFieldChange(req.custReqId, "delInchMobile1", e.target.value)} {...txtFieldProps} />
                            </Stack>
                        </Stack>
                    ))}
                </Stack>
            </ScrollBox>

            <Modal open={pickerOpen} onClose={() => trkOpReqId && setPickerOpen(false)}>
                <Box sx={pickerModalStyle} height={600}>
                    <Stack direction={'row'} justifyContent="space-between" alignItems={"center"} mb={1.2} mt={0.4} px={2}>
                        <Typography variant='h6' sx={{ fontWeight: "bold" }}>{t("bo.combShipmt.selTrkOpReq")}</Typography>
                        {trkOpReqId && <Button variant='text' onClick={() => setPickerOpen(false)}>{t("bo.updSts.cancel")}</Button>}
                    </Stack>
                    <TrkOpReqTablePopup trkOpReqRow={trkOpReqRows?.data || []} handleClose={() => setPickerOpen(false)} handleReqCLick={handlePickerSelect} />
                </Box>
            </Modal>
        </BgBox>
    )
}

export default AddCombShipmtPage;

const pickerModalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 1000,
    bgcolor: 'background.paper',
    border: '2px solid #ffff',
    boxShadow: 24,
    px: 1.5,
    py: 1.5,
    borderRadius: 2,
};
